import React from 'react';

import {Button, Icon, Menu, Text} from '@albert-io/atomic';

import type {SelectOption} from '../types';

/** A labelled group of single-select filter options rendered as check-marked menu items. */
export const FilterSection = <T extends string>({
  label,
  options,
  selected,
  onSelect
}: {
  label: string;
  options: ReadonlyArray<SelectOption<T>>;
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <>
    <Menu.Item readonly textProps={{color: 'secondary', size: 's'}}>
      {label}
    </Menu.Item>
    {options.map((option) => (
      <Menu.Item
        key={option.value}
        selected={selected === option.value}
        rightIconProps={selected === option.value ? {icon: 'check'} : undefined}
        onClick={() => onSelect(option.value)}
      >
        {option.label}
      </Menu.Item>
    ))}
  </>
);

interface FiltersMenuShellProps {
  /** Drives the brand-colored icon/label and the active dot on the trigger. */
  isActive: boolean;
  // Forwarded to the trigger button. Named `data-testid` (not `testId`) so callers write the
  // literal the e2e-selector removal guard scans for, rather than a dynamic value it can't see.
  'data-testid': string;
  disabled?: boolean;
  /**
   * Close the menu when any Menu.Item is clicked (the assignments filter behavior). Leave off when
   * closing is decided per option — e.g. an option that reveals more inputs must keep the menu open,
   * in which case the content closes it selectively via the menu context.
   */
  closeOnItemClick?: boolean;
  /** The filter sections (FilterSection / dividers / inline inputs). */
  children: React.ReactNode;
}

/**
 * The shared "Filters" trigger + dropdown shell used by the teacher classwork lists. It owns the
 * trigger button (icon + label + active dot) and the Menu; callers provide the sections as children.
 */
export const FiltersMenuShell = ({
  isActive,
  'data-testid': dataTestId,
  disabled,
  closeOnItemClick,
  children
}: FiltersMenuShellProps) => (
  <Menu closeOnItemClick={closeOnItemClick} position='bottom-end' positionFixed>
    <Menu.Trigger>
      {(triggerProps) => (
        <Button
          {...triggerProps}
          color='secondary'
          disabled={disabled}
          variant='outlined'
          data-testid={dataTestId}
        >
          <Icon className='mr-x1' color={isActive ? 'brand' : undefined} icon='bars-filter' />
          <Text color={isActive ? 'brand' : 'inherit'}>Filters</Text>
          {isActive && (
            <span aria-hidden className='ml-x1 h-x1 w-x1 background-brand rounded-full' />
          )}
        </Button>
      )}
    </Menu.Trigger>
    <Menu.Content>{children}</Menu.Content>
  </Menu>
);
